import { useEffect, useState } from 'react';
import {
  IconBan, IconArrowBackUp, IconCash, IconAlertCircle,
  IconCreditCard, IconBuildingBank,
} from '@tabler/icons-react';
import Modal from './ui/Modal';
import { useManagerApproval } from '../hooks/useManagerApproval';
import { useRole } from '../hooks/useRole';

/**
 * OrderActionsModal — Phase 2.5.
 *
 * Operations on a placed order:
 *   - Cancel  (status PENDING/PLACED only)        SUPERVISOR+
 *   - Void    (status before SERVED/CANCELLED…)   SUPERVISOR+ + manager-PIN
 *   - Refund  (status PLACED/SERVED/…)            MANAGER+    + manager-PIN
 *
 * Every operation needs a reason. Refund needs amount + which tender.
 * The buttons themselves are role-gated visible; the server enforces
 * the gate again — defence in depth.
 */

export interface OrderPayment {
  id: string;
  method: 'CASH' | 'CARD_YOCO' | 'EFT' | 'ACCOUNT' | 'VOUCHER' | 'GIFT_CARD' | 'OTHER';
  amountCents: number;
  refundedAmountCents: number;
}

export interface OrderForActions {
  id: string;
  orderNumber: number;
  status: string;
  totalCents: number;
  payments: OrderPayment[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  order: OrderForActions | null;
  onChanged?: () => void;
}

type Mode = 'idle' | 'cancel' | 'void' | 'refund';

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function methodLabel(m: OrderPayment['method']) {
  return m === 'CARD_YOCO' ? 'Card' :
         m === 'CASH' ? 'Cash' :
         m === 'EFT' ? 'EFT' :
         m === 'ACCOUNT' ? 'Account' :
         m === 'VOUCHER' ? 'Voucher' :
         m === 'GIFT_CARD' ? 'Gift card' :
         'Other';
}

function MethodIcon({ method }: { method: OrderPayment['method'] }) {
  if (method === 'CARD_YOCO') return <IconCreditCard size={16} />;
  if (method === 'EFT') return <IconBuildingBank size={16} />;
  return <IconCash size={16} />;
}

export default function OrderActionsModal({ open, onClose, order, onChanged }: Props) {
  const askApproval = useManagerApproval();
  const { hasMinRole } = useRole();
  const [mode, setMode] = useState<Mode>('idle');
  const [reason, setReason] = useState('');
  const [refundPaymentId, setRefundPaymentId] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && order) {
      setMode('idle');
      setReason('');
      setRefundPaymentId(order.payments[0]?.id ?? '');
      setRefundAmount('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, order]);

  if (!order) return null;

  const canCancel = ['PENDING', 'PLACED'].includes(order.status) && hasMinRole('SUPERVISOR');
  const canVoid = !['VOIDED', 'REFUNDED', 'CANCELLED'].includes(order.status) && hasMinRole('SUPERVISOR');
  const canRefund = !['VOIDED', 'CANCELLED'].includes(order.status) && hasMinRole('MANAGER');

  const refundAmountCents = (() => {
    const v = Number(refundAmount.replace(',', '.'));
    if (!isFinite(v) || v <= 0) return 0;
    return Math.round(v * 100);
  })();

  const selectedPayment = order.payments.find((p) => p.id === refundPaymentId);
  const refundCap = selectedPayment ? selectedPayment.amountCents - selectedPayment.refundedAmountCents : 0;

  async function submit() {
    if (!order) return;
    setError(null);

    if (mode === 'cancel') {
      if (!reason.trim()) { setError('Reason is required.'); return; }
      setSubmitting(true);
      try {
        const r = await fetch(`/api/orders/${order.id}/cancel`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim() }),
        });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error ?? `HTTP ${r.status}`);
        }
        onChanged?.();
        onClose();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mode === 'void') {
      if (!reason.trim()) { setError('Reason is required.'); return; }
      const ok = await askApproval({
        action: `Void order #${order.orderNumber}`,
        description: `Order total ${fmtR(order.totalCents)}. Voiding requires manager approval.`,
      });
      if (!ok) return;
      setSubmitting(true);
      try {
        const r = await fetch(`/api/orders/${order.id}/void`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim() }),
        });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error ?? `HTTP ${r.status}`);
        }
        onChanged?.();
        onClose();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mode === 'refund') {
      if (!reason.trim()) { setError('Reason is required.'); return; }
      if (!refundPaymentId) { setError('Select a tender to refund against.'); return; }
      if (refundAmountCents <= 0) { setError('Enter a refund amount.'); return; }
      if (refundAmountCents > refundCap) {
        setError(`Cannot refund more than ${fmtR(refundCap)} on this tender.`);
        return;
      }
      const ok = await askApproval({
        action: `Refund ${fmtR(refundAmountCents)}`,
        description: `Order #${order.orderNumber}. Refund requires manager approval.`,
      });
      if (!ok) return;
      setSubmitting(true);
      try {
        const r = await fetch(`/api/orders/${order.id}/refund`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentId: refundPaymentId,
            amountCents: refundAmountCents,
            reason: reason.trim(),
          }),
        });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error ?? `HTTP ${r.status}`);
        }
        onChanged?.();
        onClose();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSubmitting(false);
      }
      return;
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      size="lg"
      title={`Order #${order.orderNumber} · ${fmtR(order.totalCents)} · ${order.status}`}
    >
      {mode === 'idle' && (
        <>
          <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Choose an action</p>
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              disabled={!canCancel}
              onClick={() => setMode('cancel')}
              className="flex items-center gap-3 h-16 px-4 rounded-lg border-2 border-border bg-surface hover:border-warning hover:bg-warning-soft transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-md bg-warning-soft text-warning inline-flex items-center justify-center">
                <IconBan size={20} stroke={2} />
              </div>
              <div className="flex-1">
                <p className="text-15 font-bold text-heading">Cancel order</p>
                <p className="text-12 font-medium text-gray-700">Before kitchen prep · SUPERVISOR+</p>
              </div>
            </button>

            <button
              type="button"
              disabled={!canVoid}
              onClick={() => setMode('void')}
              className="flex items-center gap-3 h-16 px-4 rounded-lg border-2 border-border bg-surface hover:border-danger hover:bg-danger-soft transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-md bg-danger-soft text-danger inline-flex items-center justify-center">
                <IconBan size={20} stroke={2} />
              </div>
              <div className="flex-1">
                <p className="text-15 font-bold text-heading">Void order</p>
                <p className="text-12 font-medium text-gray-700">Cashier error · SUPERVISOR+ + manager PIN</p>
              </div>
            </button>

            <button
              type="button"
              disabled={!canRefund}
              onClick={() => setMode('refund')}
              className="flex items-center gap-3 h-16 px-4 rounded-lg border-2 border-border bg-surface hover:border-primary hover:bg-primary-soft transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-md bg-primary-soft text-primary inline-flex items-center justify-center">
                <IconArrowBackUp size={20} stroke={2} />
              </div>
              <div className="flex-1">
                <p className="text-15 font-bold text-heading">Refund</p>
                <p className="text-12 font-medium text-gray-700">Money back to customer · MANAGER+ + manager PIN</p>
              </div>
            </button>
          </div>
        </>
      )}

      {mode !== 'idle' && (
        <>
          <button
            type="button"
            onClick={() => setMode('idle')}
            className="text-13 font-bold text-primary hover:underline mb-4"
          >
            ← Back
          </button>

          {mode === 'refund' && (
            <>
              <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Refund against tender</p>
              <div className="grid grid-cols-1 gap-2 mb-4">
                {order.payments.map((p) => {
                  const refundable = p.amountCents - p.refundedAmountCents;
                  const active = refundPaymentId === p.id;
                  const disabled = refundable <= 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setRefundPaymentId(p.id)}
                      className={[
                        'flex items-center gap-3 h-14 px-3 rounded-md border-2 transition-colors text-left',
                        active ? 'border-primary bg-primary-soft' : 'border-border bg-surface hover:border-primary',
                        disabled ? 'opacity-40 cursor-not-allowed' : '',
                      ].join(' ')}
                    >
                      <div className="w-8 h-8 rounded-md bg-light text-heading inline-flex items-center justify-center">
                        <MethodIcon method={p.method} />
                      </div>
                      <div className="flex-1">
                        <p className="text-14 font-bold text-heading">{methodLabel(p.method)} · {fmtR(p.amountCents)}</p>
                        <p className="text-12 font-medium text-gray-700">
                          {p.refundedAmountCents > 0
                            ? `Already refunded ${fmtR(p.refundedAmountCents)} · ${fmtR(refundable)} remaining`
                            : `Refundable up to ${fmtR(refundable)}`}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Refund amount (R)</label>
              <input
                type="text"
                inputMode="decimal"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                placeholder={selectedPayment ? (refundCap / 100).toFixed(2) : '0.00'}
                className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-18 font-bold tabular-nums text-heading mb-3"
              />
              {selectedPayment && (
                <button
                  type="button"
                  onClick={() => setRefundAmount(((selectedPayment.amountCents - selectedPayment.refundedAmountCents) / 100).toFixed(2))}
                  className="text-12 font-bold text-primary hover:underline mb-4"
                >
                  Refund full remaining ({fmtR(selectedPayment.amountCents - selectedPayment.refundedAmountCents)})
                </button>
              )}
            </>
          )}

          <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
            Reason <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              mode === 'cancel' ? 'e.g. customer changed mind' :
              mode === 'void' ? 'e.g. wrong product rung up' :
              'e.g. spilled latte, customer dissatisfied'
            }
            className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-15 font-semibold text-heading mb-4"
          />

          {error && (
            <div className="mb-4 rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
              <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
              <p className="text-13 font-semibold text-danger">{error}</p>
            </div>
          )}

          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => setMode('idle')}
              disabled={submitting}
              className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 disabled:opacity-50 transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className={[
                'flex-[2] h-12 rounded-md text-[#FFFFFF] text-15 font-bold transition-colors shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed',
                mode === 'cancel' ? 'bg-warning hover:bg-warning-hover' :
                mode === 'void' ? 'bg-danger hover:bg-danger-hover' :
                'bg-primary hover:bg-primary-hover',
              ].join(' ')}
            >
              {submitting ? 'Working…' :
                mode === 'cancel' ? 'Confirm cancel' :
                mode === 'void' ? 'Confirm void' :
                `Refund ${refundAmountCents > 0 ? fmtR(refundAmountCents) : ''}`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
